/**
 * Shared theme utilities for ECharts-based chart components.
 * Uses the centralized themeDefaults tokens so chart colors stay in sync
 * with the rest of the dees-catalog design system.
 *
 * ECharts renders on <svg> and cannot read CSS custom properties,
 * so we reference the TypeScript source-of-truth (themeDefaults) directly.
 *
 * IMPORTANT: All colors passed to ECharts for data series must be hex or rgb/rgba.
 * ECharts cannot interpolate HSL strings during hover/emphasis animations,
 * causing them to flash black.
 */

import { themeDefaults } from '../00theme.js';
import { systemFontFamily } from '../00fonts.js';

const light = themeDefaults.colors.light;
const dark = themeDefaults.colors.dark;

/**
 * Series color palette for ECharts charts.
 * Aligned with the system color palette used throughout the design system.
 * All values are hex — ECharts requires this for animation interpolation
 * (HSL strings flash black during hover/emphasis).
 */
const SERIES_COLORS = {
  dark: [
    '#0a84ff',  // systemBlue (dark)
    '#64d2ff',  // systemCyan (dark)
    '#bf5af2',  // systemPurple (dark)
    '#ff9f0a',  // systemOrange (dark)
    '#30d158',  // systemGreen (dark)
    '#ff375f',  // systemPink (dark)
  ],
  light: [
    '#007aff',  // systemBlue
    '#32ade6',  // systemCyan
    '#af52de',  // systemPurple
    '#ff9500',  // systemOrange
    '#34c759',  // systemGreen
    '#ff2d55',  // systemPink
  ],
};

export function getEchartsSeriesColors(goBright: boolean): string[] {
  return goBright ? SERIES_COLORS.light : SERIES_COLORS.dark;
}

/**
 * Convert a hex color to an rgba string with the given alpha.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getEchartsThemeOptions(goBright: boolean): Record<string, any> {
  const colors = goBright ? light : dark;
  return {
    backgroundColor: 'transparent',
    textStyle: {
      color: colors.textSecondary,
      fontFamily: systemFontFamily,
      fontSize: 12,
    },
    // No global `color` array — each component sets per-item/per-series
    // colors explicitly to avoid conflicts during emphasis animations.
    tooltip: {
      backgroundColor: colors.bgPrimary,
      borderColor: colors.borderDefault,
      textStyle: {
        color: colors.textPrimary,
        fontSize: 12,
      },
      confine: true,
    },
    legend: {
      textStyle: {
        color: colors.textSecondary,
        fontSize: 12,
      },
    },
  };
}

/**
 * Helper to get the resolved theme colors object for use in buildOption().
 */
export function getThemeColors(goBright: boolean) {
  return goBright ? light : dark;
}
